import React from 'react';
import PropTypes from 'prop-types';
import axios from 'cccisd-axios';
import IconBell from 'cccisd-icons/svg/bell2';
import IconCart from 'cccisd-icons/cart';
import IconCheckmark from 'cccisd-icons/svg/checkmark';
import IconLeft from 'cccisd-icons/arrow-left6';
import IconUser from 'cccisd-icons/user';
import AlertButton from 'cccisd-laravel-nexus/dist/components/AlertButton';
import Tooltip from 'cccisd-tooltip';
import { Link, useLocation } from 'cccisd-react-router';
import wustlLogo from './wustl.svg';
import style from './style.css';

const wpUrl = window.cccisd.env.wpUrl || 'https://familybasedbehavioraltreatment.wustl.edu';
const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

function Header({ className, isWordpress, noLogoLink, allowSwitchRoles }) {
    const location = useLocation();
    const currPath = location.pathname;
    const wpOnlyLinks = [
        { to: wpUrl, label: 'Home' },
        { to: wpUrl + '/treatment-overview', label: 'Treatment Overview' },
        { to: wpUrl + '/new-training', label: 'Training' },
        { to: wpUrl + '/about-us', label: 'About Us' },
        { to: wpUrl + '/contact', label: 'Contact' },
        { to: wpUrl + '/portals', label: 'Portals' },
        { to: wpUrl + '/cart', label: <IconCart /> },
    ];

    const laravelLinksPerRole = {
        guest: [
            { to: wpUrl, label: 'Home' },
            { to: '/login', label: 'Log In', active: /^\/login/i.test(currPath) },
        ],
        uberadmin: [
            { to: '/activity', label: 'Activity', active: /^\/activity/i.test(currPath) },
            {
                to: '/manage',
                label: 'Manage',
                active: /^\/manage/i.test(currPath),
                children: [
                    { to: '/manage#instructor', label: 'Manage - FBT' },
                    { to: '/manage-tfh', label: 'Manage - Together For Health' },
                ],
            },
            { to: '/courses', label: 'Courses', children: [{ to: '/survey/training', label: 'FBT Training' }] },
            {
                to: '/resources',
                label: 'Resources',
                children: [
                    { to: '/resources/admin', label: 'Admin' },
                    { to: '/child', label: 'Child Resources' },
                    { to: '/parent', label: 'Parent Resources' },
                ],
            },
        ],
        instructor: [
            { to: '/manage', label: 'Manage', active: /^\/manage/i.test(currPath) },
            { to: '/dashboard', label: 'Dashboard', active: /^\/dashboard/i.test(currPath) },
            { to: '/survey/training', label: 'Training', active: /^\/d\//i.test(currPath) },
            { to: '/parent', label: 'FBT Parent Resources', active: /^\/parent/i.test(currPath) },
            { to: '/child', label: 'FBT Child Resources', active: /^\/child/i.test(currPath) },
        ],
        parent: [
            { to: '/parent', label: 'Parent Resources', active: /^\/parent/i.test(currPath) },
            { to: '/child', label: 'Child Resources', active: /^\/child/i.test(currPath) },
            { to: '/manage', label: 'Manage', active: /^\/manage/i.test(currPath) },
        ],
        child: [{ to: '/child', label: 'Child Resources', active: /^\/child/i.test(currPath) }],
        tfhSupervisor: [],
        tfhInstructor: [
            { to: '/dashboard', label: 'Dashboard', active: /^\/dashboard/i.test(currPath) },
            { to: '/program', label: 'Program', active: /^\/program/i.test(currPath) },
            { to: '/messages', label: 'Messages', active: /^\/messages/i.test(currPath) },
            { to: '/account/alerts', label: 'Alerts', active: /^\/account\/alerts/i.test(currPath), isAlerts: true },
        ],
        tfhParent: [
            { to: '/tracking', label: 'Tracking', active: /^\/tracking/i.test(currPath) },
            { to: '/trackingReport', label: 'Tracking Report', active: /^\/trackingReport/i.test(currPath) },
            { to: '/program', label: 'Program', active: /^\/program/i.test(currPath) },
            { to: '/messages', label: 'Messages', active: /^\/messages/i.test(currPath) },
            { to: '/account/alerts', label: 'Alerts', active: /^\/account\/alerts/i.test(currPath), isAlerts: true },
        ],
        tfhChild: [
            { to: '/tracking', label: 'Tracking', active: /^\/tracking/i.test(currPath) },
            { to: '/trackingReport', label: 'Tracking Report', active: /^\/trackingReport/i.test(currPath) },
        ],
    };

    function renderNavItem(navItem, isChild = false) {
        if (Array.isArray(navItem.children)) {
            return (
                <li className="nav-item dropdown" key={navItem.to}>
                    <a
                        className="nav-link dropdown-toggle"
                        href="#"
                        role="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        {navItem.label}
                    </a>
                    <ul className="dropdown-menu">{navItem.children.map(def => renderNavItem(def, true))}</ul>
                </li>
            );
        }

        let liClass = isChild ? '' : `nav-item ${navItem.active ? 'active ' : ''}`;
        const anchorClass = isChild ? 'dropdown-item' : 'nav-link';

        let anchor;
        if (navItem.isAlerts) {
            liClass += 'dropdown';
            anchor = (
                <AlertButton
                    render={({ content, alertCount }) => (
                        <Tooltip
                            title={<div className={style.alertContent}>{content}</div>}
                            placement="bottom"
                            tippyProps={{ theme: 'light', trigger: 'click', interactive: true }}
                        >
                            <a className="dropdown-toggle nav-link">
                                {alertCount > 0 && <div className={style.alertCounter}>{Math.min(alertCount, 99)}</div>}
                                <span className={style.alertBell}>
                                    <IconBell />
                                </span>
                            </a>
                        </Tooltip>
                    )}
                />
            );
        } else if (navItem.to.startsWith('http')) {
            anchor = (
                <a href={navItem.to} className={anchorClass}>
                    {navItem.label}
                </a>
            );
        } else {
            anchor = (
                <Link to={navItem.to} className={anchorClass}>
                    {navItem.label}
                </Link>
            );
        }

        return (
            <li className={liClass} key={navItem.to}>
                {anchor}
            </li>
        );
    }

    function renderLogoRow() {
        let logo = <img src={wustlLogo} alt="Washington University of St. Louis" className={style.logo} />;
        if (!noLogoLink) {
            logo = <a href={wpUrl}>{logo}</a>;
        }

        return (
            <div className={style.logoRow}>
                <div className={className}>{logo}</div>
            </div>
        );
    }

    function renderTitleRow() {
        return (
            <div className={style.titleRow}>
                <div className={className}>
                    <div className={style.title}>
                        <h1>
                            {isWordpress
                                ? 'Family-based Behavioral Treatment'
                                : 'Family-based Behavioral Treatment (FBT)'}
                        </h1>
                    </div>
                </div>
            </div>
        );
    }

    function renderLinksRow() {
        let navItems = [];
        if (isWordpress) {
            navItems = wpOnlyLinks;
        } else {
            navItems = laravelLinksPerRole[Fortress.user.acting.role.handle];
        }

        const adminDropdown = Fortress.isSuperUser() && (
            <li className="nav-item dropdown">
                <a
                    className="nav-link dropdown-toggle"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    Admin
                </a>
                <ul className="dropdown-menu">
                    <li>
                        <Link className="dropdown-item" to="/appdefs/about">
                            About
                        </Link>
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/appdefs/logs">
                            Logs
                        </Link>
                    </li>
                    <li>
                        <hr className="dropdown-divider" />
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/quest">
                            Quests
                        </Link>
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/resources/admin">
                            Resources
                        </Link>
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/shorturls">
                            Short URLs
                        </Link>
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/licensed-products">
                            Licensed Products
                        </Link>
                    </li>
                    <li>
                        <hr className="dropdown-divider" />
                    </li>
                    <li>
                        <Link className="dropdown-item" to="/appdefs/admin/json-editor">
                            App Settings
                        </Link>
                    </li>
                </ul>
            </li>
        );

        const roles = Array.isArray(Fortress?.user?.pawns)
            ? Fortress.user.pawns.filter(r => !['Guest', 'Respondent'].includes(r.name))
            : [];
        const userDropdown = Fortress.auth() && (
            <li className="nav-item dropdown">
                <a
                    className="nav-link dropdown-toggle"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    <IconUser />
                </a>
                <ul className="dropdown-menu">
                    <p className={`px-3 ${style.currUsername}`}>
                        <small>
                            Logged in as <b>{Fortress.user.acting.user.username}</b>
                        </small>
                    </p>
                    {Fortress.user.acting.id !== Fortress.user.actual.id && (
                        <li>
                            <a
                                className="dropdown-item"
                                href="#"
                                onClick={async e => {
                                    e.preventDefault();
                                    await axios.get(Boilerplate.route('api.nexus.user.logoutAs'));
                                    window.location.href = Boilerplate.url(Fortress.settings.config.after_login_url);
                                }}
                            >
                                <IconLeft spaceRight />
                                RETURN TO {Fortress.user.actual.user.username}
                            </a>
                        </li>
                    )}
                    {Fortress.user.acting.id === Fortress.user.actual.id &&
                        roles.length > 1 &&
                        roles.map(pawn => (
                            <li>
                                <Link
                                    key={pawn.id}
                                    to="/"
                                    className="dropdown-item"
                                    onClick={async e => {
                                        e && e.preventDefault();

                                        if (allowSwitchRoles) {
                                            return;
                                        }

                                        if (pawn.id === Fortress.user.pawnId) {
                                            return;
                                        }

                                        const response = await axios.post(
                                            Boilerplate.route('api.nexus.user.changeTo'),
                                            {
                                                pawn: pawn.id,
                                            }
                                        );

                                        const redirectUrl = response.data.data.hasIncompleteSiteRegistration
                                            ? Boilerplate.url('/account/registrationSurvey')
                                            : Boilerplate.url(Fortress.settings.config.after_login_url);

                                        window.location.href = redirectUrl;
                                    }}
                                >
                                    {pawn.id === Fortress.user.pawnId ? (
                                        <IconCheckmark />
                                    ) : (
                                        <svg viewBox="0 0 1024 1024">
                                            <g />
                                        </svg>
                                    )}
                                    {pawn.name}
                                </Link>
                            </li>
                        ))}
                    {Fortress.user.acting.id === Fortress.user.actual.id && Fortress.user.user.auth_type !== 'ldap' && (
                        <>
                            <li>
                                <hr className="dropdown-divider" />
                            </li>
                            <li>
                                <Link to="/account/profile" className="dropdown-item">
                                    Update profile
                                </Link>
                            </li>
                            <li>
                                <Link to="/account/updatePassword" className="dropdown-item">
                                    Change password
                                </Link>
                            </li>
                            <li>
                                <hr className="dropdown-divider" />
                            </li>
                        </>
                    )}
                    <li>
                        <hr className="dropdown-divider" />
                    </li>
                    <li>
                        <a className="dropdown-item" href={Boilerplate.url('api/user/logout')}>
                            Log Out
                        </a>
                    </li>
                </ul>
            </li>
        );

        return (
            <div className={style.linksRow}>
                <nav className="navbar navbar-expand-lg py-1" data-bs-theme="dark">
                    <div className={className}>
                        <button
                            className="navbar-toggler"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#navbarSupportedContent"
                            aria-controls="navbarSupportedContent"
                            aria-expanded="false"
                            aria-label="Toggle navigation"
                        >
                            <span className="navbar-toggler-icon" />
                        </button>
                        <div className="collapse navbar-collapse" id="navbarSupportedContent">
                            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                                {navItems.map(def => renderNavItem(def))}
                                {adminDropdown}
                                {userDropdown}
                            </ul>
                        </div>
                    </div>
                </nav>
            </div>
        );
    }

    return (
        <div className={style.header}>
            {renderLogoRow()}
            {renderTitleRow()}
            {renderLinksRow()}
        </div>
    );
}

Header.propTypes = {
    className: PropTypes.string,
    noLogoLink: PropTypes.bool,
    isWordpress: PropTypes.bool,
    allowSwitchRoles: PropTypes.bool,
};

Header.defaultProps = {
    className: 'container',
    noLogoLink: false,
    isWordpress: false,
    allowSwitchRoles: false,
};

export default Header;
